import { Breadcrumb, Button as AntButton, Form, Input, InputNumber, Select } from 'antd'
import axios from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { ScalingSpecReq } from '@/api/type-request.ts'
import { ServerScaleSpecInfo } from '@/api/type-server.ts'
import AddIcon from '@/assets/icons/add.svg?react'
import ArrowDownIcon from '@/assets/icons/arrow-down.svg?react'
import LeftArrow from '@/assets/icons/left-arrow.svg?react'
import RefreshIcon from '@/assets/icons/reset.svg?react'
import { Divider, Loading, message, Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Textarea } from '@/components/base/Textarea'
import { EmptyView } from '@/components/EmptyView.tsx'
import { getScaleListPath, useUnifiedContext } from '@/contexts/UnifiedContext'
import { Instance, RunningStatuses } from '@/models/instance.ts'
import { Metric } from '@/models/metric.ts'
import { AddMetricModal } from '@/pages/auto-scaling/components/AddMetricModal.tsx'
import { MissingMetricModal } from '@/pages/auto-scaling/components/MissingMetricModal.tsx'
import { formatRamMB } from '@/utils/formatter.ts'
type FormValuesType = {
  name: string
  resourceType: number
  instanceId: number
  minReplicas: number
  maxReplicas: number
  metricsDesc: string
  workerSystemEnv: string
  workerHardwareResource: string
  cmd: string
  envs: string
}

type PortConfig = {
  protocol: number // 1 = Https (disabled), 2 = Http
  port: number | undefined
}

export function ScalingCreateSubPage() {
  const location = useLocation()
  const editingSpecResId: string = location.state?.editingSpecResId
  const navigate = useNavigate()
  const ctx = useUnifiedContext()
  const scaleListPath = getScaleListPath(ctx)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm<FormValuesType>()
  const [instanceList, setInstanceList] = useState<Instance[]>([])
  const [selectedInstance, setSelectedInstance] = useState<Instance | undefined>()
  const [metrics, setMetrics] = useState<Metric[]>([])
  const [missingMetricModalOpen, setMissingMetricModalOpen] = useState(false)
  const [editingMetric, setEditingMetric] = useState<Metric | undefined>()
  const [addMetricModalOpen, setAddMetricModalOpen] = useState(false)
  const [editingSpec, setEditingSpec] = useState<ServerScaleSpecInfo>()
  const [portConfigs, setPortConfigs] = useState<PortConfig[]>([{ protocol: 2, port: undefined }])
  const { t } = useTranslation(['scaling', 'translation'])

  const [metricPreviews, setMetricPreviews] = useState<
    {
      loading: boolean
      isOpen: boolean
      result: {
        status: string
        signal: string
        data: string
      }
    }[]
  >([])

  const getScaleSpec = useCallback(
    async (specId: string) => {
      setLoading(true)
      try {
        const res = await API.getScaleSpec({
          spec_id: specId,
        })
        if (res.code === 0) {
          setEditingSpec(res.spec)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [editingSpecResId],
  )

  useEffect(() => {
    if (editingSpecResId) getScaleSpec(editingSpecResId)
  }, [editingSpecResId])

  useEffect(() => {
    if (editingSpec) {
      form.setFieldsValue({
        name: editingSpec.class,
        resourceType: 1,
        minReplicas: editingSpec.minReplicas,
        maxReplicas: editingSpec.maxReplicas,
        cmd: editingSpec.runtimeEnvs[0].systemEnv.cmd,
      })
      setMetrics(editingSpec.metrics)
      setMetricPreviews(
        editingSpec.metrics.map(() => ({
          loading: false,
          isOpen: false,
          result: { status: '', signal: '', data: '' },
        })),
      )
      // Initialize portConfigs from extraPorts array
      const extraPorts = editingSpec.runtimeEnvs[0].systemEnv.extraPorts
      if (extraPorts && extraPorts.length > 0) {
        setPortConfigs(extraPorts.map((port) => ({ protocol: 2, port })))
      }
    }
  }, [editingSpec, form])

  const handleSaveMetric = (newMetric: Metric) => {
    if (editingMetric) {
      setMetrics((prevMetrics) =>
        prevMetrics.map((m) => (m.name === editingMetric.name ? { ...m, ...newMetric } : m)),
      )
    } else {
      setMetrics((prevMetrics) => [...prevMetrics, newMetric])
      setMetricPreviews((prev) => [
        ...prev,
        {
          loading: false,
          isOpen: false,
          result: { status: '', signal: '', data: '' },
        },
      ])
    }
    setEditingMetric(undefined)
  }

  const onPreviewClick = (index: number) => {
    setMetricPreviews((prev) => {
      const newPreviews = [...prev]
      newPreviews[index].loading = true
      return newPreviews
    })
    axios
      .get(metrics[index].url)
      .then((res) => {
        setMetricPreviews((prev) => {
          const newPreviews = [...prev]
          newPreviews[index].result = {
            status: res.status.toString(),
            signal: '🟢',
            data: JSON.stringify(res.data, null, 2),
          }
          return newPreviews
        })
      })
      .catch((error) => {
        setMetricPreviews((prev) => {
          const newPreviews = [...prev]
          newPreviews[index].result = {
            status: error.response?.status.toString() || 'Unknown',
            signal: '🔴',
            data: JSON.stringify(error.message, null, 2),
          }
          return newPreviews
        })
      })
      .finally(() => {
        setMetricPreviews((prev) => {
          const newPreviews = [...prev]
          newPreviews[index].loading = false
          return newPreviews
        })
      })
  }

  const handleAddPort = () => {
    setPortConfigs((prev) => [...prev, { protocol: 2, port: undefined }])
  }

  const handleRemovePort = (index: number) => {
    setPortConfigs((prev) => prev.filter((_, i) => i !== index))
  }

  const handlePortChange = (
    index: number,
    field: 'protocol' | 'port',
    value: number | undefined,
  ) => {
    setPortConfigs((prev) =>
      prev.map((config, i) => (i === index ? { ...config, [field]: value } : config)),
    )
  }

  const getPortLabel = (baseKey: string, index: number) => {
    if (portConfigs.length === 1) {
      return <Trans i18nKey={`createSubPage.form.${baseKey}`} ns="scaling" />
    }
    return (
      <>
        <Trans i18nKey={`createSubPage.form.${baseKey}`} ns="scaling" />
        {`-${index + 1}`}
      </>
    )
  }

  const handleSubmit = (values: FormValuesType) => {
    setLoading(true)

    if (!selectedInstance) return
    const payload: ScalingSpecReq = {
      class: values.name,
      max_replicas: values.maxReplicas,
      min_replicas: values.minReplicas,
      metrics_desc: JSON.stringify(metrics),
      worker_system_env: JSON.stringify({
        fromImageId: selectedInstance.node.image.id,
        fromSnapshotId: selectedInstance.fromSnapshot?.id,
        fromImageName: selectedInstance.node.image.alias,
        fromImageDescription: selectedInstance.node.image.description,
        cmd: values.cmd,
        envs: selectedInstance.envs,
        extraPorts: portConfigs
          .filter((config) => config.port !== undefined)
          .map((config) => config.port),
      }),
      worker_hardware_resource: JSON.stringify({
        agentDomains: [selectedInstance.domain],
        attributeTags: 1,
        cpus: selectedInstance.hardware.machine.cpuIds.length,
        cpuName: selectedInstance.hardware.machine.cpuName,
        diskGB: selectedInstance.hardware.machine.diskGB,
        ramMB: selectedInstance.hardware.machine.ramMB,
        gpus: selectedInstance.hardware.gpu.gpuIds.length,
        gpuName: selectedInstance.hardware.gpu.gpuName,
        gpuRamMB: selectedInstance.hardware.gpu.gpuRamMB,
        unitQty: 1,
      }),
      enabled: true,
    }

    if (editingSpec) {
      API.updateScaleSpecRule({
        spec_id: editingSpecResId,
        ...payload,
      }).then((res) => {
        if (res.code == 0) {
          message.success('Update successfully')
          navigate(scaleListPath)
        }
      })
    } else {
      API.createScalingSpec(payload)
        .then((res) => {
          if (res.code == 0) {
            message.success('Create successfully')
            navigate(scaleListPath)
          }
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const getNodeList = useCallback(async () => {
    try {
      const res = await API.getNodeList({
        page: 1,
        per_page: 10,
        statuses: RunningStatuses.join(','),
        region_filter_criteria: 0,
      })

      setInstanceList(res.userNodes.map((data) => new Instance(data)))
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    getNodeList()
  }, [])

  return (
    <>
      <div className="card bg-panel-white">
        <div className="flex items-center space-x-4 mb-4">
          <Link to={scaleListPath}>
            <div className="w-11 h-11 icon-btn">
              <LeftArrow />
            </div>
          </Link>
          <Breadcrumb
            items={[
              {
                title: (
                  <Link to={scaleListPath} className="h6 text-secondary !hover:text-red-600">
                    <Trans i18nKey="title" ns="scaling" />
                  </Link>
                ),
              },
              {
                title: (
                  <span className="h6 text-primary">
                    {editingSpecResId ? (
                      <Trans i18nKey="createSubPage.editTitle" ns="scaling" />
                    ) : (
                      <Trans i18nKey="createSubPage.title" ns="scaling" />
                    )}
                  </span>
                ),
              },
            ]}
            separator={<span className="h6 text-primary">/</span>}
          />
        </div>

        <Divider />
        <Form
          form={form}
          validateTrigger={['onChange', 'onBlur']}
          layout="vertical"
          name="create-scaling-spec-form"
          scrollToFirstError={true}
          initialValues={{
            resourceType: 1,
          }}
        >
          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="createSubPage.form.name" ns="scaling">
                  Rules Name
                </Trans>
              </div>
            }
            id={'name'}
            name="name"
            className="mb-8"
            rules={[{ required: true, message: t('createSubPage.form.namePlaceholder') }]}
          >
            <Input
              className="h-11 px-4 py-3 t-sb border-neutral-200 w-[576px]"
              disabled={!!editingSpec}
            />
          </Form.Item>
          <Form.Item
            label={
              <span className="t-sb text-primary">
                <Trans i18nKey="createSubPage.form.resourceType" ns="scaling" />
                <span className="ml-1 text-error">*</span>
              </span>
            }
            name="resourceType"
            className="mb-8 w-[576px]"
            rules={[{ required: true, message: '' }]}
          >
            <Select
              suffixIcon={<ArrowDownIcon width={20} height={20} />}
              className="h-11 t-sb !outline-neutral-200"
              rootClassName="t-sb !outline-neutral-200"
              variant="outlined"
            >
              <Select.Option value={1}>
                <Trans i18nKey="createSubPage.form.resourceTypeInstance" ns="scaling" />
              </Select.Option>
            </Select>
          </Form.Item>

          <div className="mb-8 py-2">
            <span className="h6 pl-2 border-l-4 border-secondary">
              <Trans i18nKey="createSubPage.form.scalingTargetTitle" ns="scaling" />
            </span>
          </div>

          <Form.Item
            label={
              <span className="t-sb text-primary">
                <Trans i18nKey="createSubPage.form.scalingTargetInstance" ns="scaling" />
                <span className="ml-1 text-error">*</span>
              </span>
            }
            name="instanceId"
            className="mb-8 w-[576px]"
            rules={[
              {
                required: true,
                message: t('createSubPage.form.scalingTargetInstancePlaceholder'),
              },
            ]}
          >
            <Select
              showSearch
              value={selectedInstance?.id}
              suffixIcon={<ArrowDownIcon width={20} height={20} />}
              className="h-11 t-sb"
              rootClassName="t-sb"
              placeholder={t('createSubPage.form.scalingTargetInstancePlaceholder')}
              onChange={(value) => {
                const instance = instanceList.find((inst) => inst.id === value)
                setSelectedInstance(instance)
                form.setFieldsValue({ instanceId: value })
              }}
              filterOption={(input, option) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }}
              options={instanceList.map((instance) => ({
                value: instance.id,
                label: `${instance.resID} - ${instance.hardware?.gpu?.gpuName || instance.hardware?.machine?.cpuName}`,
              }))}
            ></Select>
          </Form.Item>

          <div className="p-4 grid grid-cols-4 gap-y-4 bg-panel-gray-100 rounded-lg mb-8">
            <div className="col-span-4 grid grid-cols-4 gap-x-4">
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="createSubPage.form.spec.image" ns="scaling" />
                </div>
                <div className="t-sb text-primary">
                  {selectedInstance?.rentInfos.image.alias || '-'}
                </div>
              </div>
              <div className="col-span-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="createSubPage.form.spec.imageDesc" ns="scaling" />
                </div>
                <div className="t-sb text-primary">
                  {selectedInstance?.rentInfos.image.description || '-'}
                </div>
              </div>
              {selectedInstance?.fromSnapshot && (
                <div className="col-span-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="createSubPage.form.spec.snapshot" ns="scaling">
                      Snapshot
                    </Trans>
                  </div>
                  <div className="t-sb text-primary">
                    {selectedInstance.fromSnapshot.subject || '-'}
                  </div>
                </div>
              )}
            </div>
            <div className="col-span-4 grid grid-cols-4 gap-x-4">
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="createSubPage.form.spec.gpuModel" ns="scaling" />
                </div>
                <div className="t-sb text-primary">
                  {selectedInstance?.hardware?.gpu?.gpuName || '-'}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="createSubPage.form.spec.gpus" ns="scaling" />
                </div>
                <div className="t-sb text-primary">
                  {selectedInstance?.hardware?.gpu?.gpuIds.length || '-'}
                </div>
              </div>
              <div className="col-span-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="createSubPage.form.spec.gpuRam" ns="scaling" />
                </div>
                <div className="t-sb text-primary">
                  {formatRamMB(selectedInstance?.hardware?.gpu?.gpuRamMB || 0)}
                </div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 gap-x-4">
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="createSubPage.form.spec.cpuModel" ns="scaling" />
                </div>
                <div className="t-sb text-primary">
                  {selectedInstance?.hardware?.machine?.cpuName || '-'}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="createSubPage.form.spec.vCpus" ns="scaling" />
                </div>
                <div className="t-sb text-primary">
                  {selectedInstance?.hardware?.machine?.cpuIds.length || '-'}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="createSubPage.form.spec.ram" ns="scaling" />
                </div>
                <div className="t-sb text-primary">
                  {formatRamMB(selectedInstance?.hardware?.machine?.ramMB || 0)}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="createSubPage.form.spec.storage" ns="scaling" />
                </div>
                <div className="t-sb text-primary">
                  {selectedInstance?.hardware?.machine?.diskGB || 0} GB
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            {portConfigs.map((config, index) => (
              <div key={index} className="flex justify-start items-end space-x-4">
                <Form.Item
                  label={<div className="t-sb">{getPortLabel('protocol', index)}</div>}
                  className="w-[576px] mb-0"
                >
                  <Select
                    value={config.protocol}
                    onChange={(val) => handlePortChange(index, 'protocol', val)}
                    suffixIcon={<ArrowDownIcon width={20} height={20} />}
                    className="h-11"
                  >
                    <Select.Option value={1} disabled>
                      Https
                    </Select.Option>
                    <Select.Option value={2}>Http</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label={<div className="t-sb">{getPortLabel('port', index)}</div>}
                  className="w-[576px] mb-0"
                >
                  <InputNumber
                    value={config.port}
                    onChange={(val) => handlePortChange(index, 'port', val ?? undefined)}
                    className="h-11 t-sb w-full flex items-center border-neutral-200"
                    placeholder={t('createSubPage.form.portPlaceholder')}
                    min={0}
                    max={65535}
                  />
                </Form.Item>

                <div className="flex items-center space-x-2 h-11">
                  {portConfigs.length >= 2 && (
                    <Button className="w-[88px] h-11" onClick={() => handleRemovePort(index)}>
                      <Trans i18nKey="createSubPage.action.remove" ns="scaling">
                        Remove
                      </Trans>
                    </Button>
                  )}
                  {index === portConfigs.length - 1 && (
                    <Button type="secondary" className="w-[88px] h-11" onClick={handleAddPort}>
                      <Trans i18nKey="createSubPage.action.add" ns="scaling">
                        Add
                      </Trans>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <Form.Item
            label={
              <div className="t-sb">
                <Trans i18nKey="createSubPage.form.cmd" ns="scaling" />
              </div>
            }
            name="cmd"
            className="w-[576px]"
          >
            <Textarea
              className="h-11 px-4 py-3 t-sb"
              placeholder={t('createSubPage.form.cmdPlaceholder')}
            />
          </Form.Item>

          <div className="mb-8 py-2">
            <span className="h6 pl-2 border-l-4 border-secondary">
              <Trans i18nKey="createSubPage.form.scalingRulesTitle" ns="scaling" />
            </span>
          </div>

          <div className="flex justify-start items-center space-x-4">
            <Form.Item
              label={
                <div className="text-with-star t-sb">
                  <Trans i18nKey="createSubPage.form.minQuantity" ns="scaling">
                    Elastic Quantity Control Minimum
                  </Trans>
                </div>
              }
              name="minReplicas"
              dependencies={['maxReplicas']}
              rules={[
                { required: true, message: t('createSubPage.form.minQuantityPlaceholder') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const maxValue = getFieldValue('maxReplicas')
                    if (value !== undefined && maxValue !== undefined && value > maxValue) {
                      return Promise.reject(
                        new Error(
                          t('createSubPage.form.minCannotExceedMax', 'Min cannot exceed max'),
                        ),
                      )
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
              className="w-[576px]"
            >
              <InputNumber
                className="w-full t-sb px-1 border-neutral-200"
                size="large"
                min={0}
                controls={false}
                placeholder={t('createSubPage.form.minQuantityPlaceholder')}
              />
            </Form.Item>
            <Form.Item
              label={
                <div className="text-with-star t-sb">
                  <Trans i18nKey="createSubPage.form.maxQuantity" ns="scaling">
                    Elastic Quantity Control Maximum
                  </Trans>
                </div>
              }
              name="maxReplicas"
              dependencies={['minReplicas']}
              rules={[
                { required: true, message: t('createSubPage.form.maxQuantityPlaceholder') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const minValue = getFieldValue('minReplicas')
                    if (value !== undefined && minValue !== undefined && value < minValue) {
                      return Promise.reject(
                        new Error(
                          t(
                            'createSubPage.form.maxCannotBeLessThanMin',
                            'Max cannot be less than min',
                          ),
                        ),
                      )
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
              className="w-[576px]"
            >
              <InputNumber
                className="w-full t-sb px-1 border-neutral-200"
                size="large"
                min={1}
                controls={false}
                placeholder={t('createSubPage.form.maxQuantityPlaceholder')}
              />
            </Form.Item>
          </div>
          <div className="t-sb mb-2">
            <Trans i18nKey="createSubPage.form.loadMetricTitle" ns="scaling" />
          </div>
          <div className="bg-panel-gray-100 rounded-lg p-4 ">
            {metrics.length === 0 && (
              <EmptyView
                content={
                  <>
                    <div className="t-sm mb-4 text-center">
                      <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
                    </div>
                    <AntButton
                      type="primary"
                      className="px-3 py-2 t-x-sb flex items-center justify-center"
                      onClick={() => {
                        setAddMetricModalOpen(true)
                      }}
                    >
                      <AddIcon height={16} width={16} />
                      <Trans i18nKey="createSubPage.form.moreLoadMetricBtn" ns="scaling" />
                    </AntButton>
                  </>
                }
              />
            )}

            {metrics.length > 0 && (
              <>
                <table className="table-card table-fixed">
                  <thead>
                    <tr>
                      <th colSpan={1} className="!t-x-sb !text-tertiary">
                        <Trans
                          i18nKey="createSubPage.addMetricModal.form.metricName"
                          ns="scaling"
                        />
                      </th>
                      <th colSpan={2} className="!t-x-sb !text-tertiary">
                        <Trans
                          i18nKey="createSubPage.addMetricModal.form.description"
                          ns="scaling"
                        />
                      </th>
                      <th colSpan={2} className="!t-x-sb !text-tertiary">
                        <Trans
                          i18nKey="createSubPage.addMetricModal.form.baselineType"
                          ns="scaling"
                        />
                      </th>
                      <th colSpan={1} className="!t-x-sb !text-tertiary">
                        <Trans
                          i18nKey="createSubPage.addMetricModal.form.baselineValue"
                          ns="scaling"
                        />
                      </th>
                      <th colSpan={3} className="!t-x-sb !text-tertiary">
                        <Trans
                          i18nKey="createSubPage.addMetricModal.form.retrieveAddress"
                          ns="scaling"
                        />
                      </th>
                      <th colSpan={1} className="!t-x-sb !text-tertiary">
                        <Trans i18nKey="tableHeaderAction" ns="scaling">
                          Action
                        </Trans>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="t-x-sb text-secondary bg-panel-white">
                    {metrics.map((metric, index) => (
                      <>
                        <tr key={index}>
                          <td colSpan={1} className="px-4 py-3">
                            {metric.name}
                          </td>
                          <td colSpan={2} className="px-4 py-3">
                            {metric.desc}
                          </td>
                          <td colSpan={2} className="px-4 py-3">
                            {metric.target.type === 3 && 'Numeric Baseline'}
                            {metric.target.type === 1 && 'Ratio Baseline'}
                          </td>
                          <td colSpan={1} className="px-4 py-3">
                            {metric.target.value}
                          </td>
                          <td colSpan={3} className="px-4 py-3">
                            {metric.url || '-'}
                          </td>
                          <td colSpan={2} className="px-4 py-3">
                            <div className="flex gap-x-4">
                              <div
                                className="t-x-sb text-highlight cursor-pointer"
                                onClick={() => {
                                  setEditingMetric(metric)
                                  setAddMetricModalOpen(true)
                                }}
                              >
                                <Trans i18nKey="createSubPage.action.edit" ns="scaling">
                                  Edit
                                </Trans>
                              </div>
                              <div
                                className="t-x-sb text-highlight cursor-pointer"
                                onClick={() => {
                                  setMetricPreviews((prev) => {
                                    const newPreviews = [...prev]
                                    newPreviews[index].isOpen = !newPreviews[index].isOpen
                                    return newPreviews
                                  })

                                  if (metricPreviews[index].isOpen) {
                                    onPreviewClick(index)
                                  }
                                }}
                              >
                                {metricPreviews[index].isOpen ? (
                                  <Trans i18nKey="createSubPage.action.close" ns="scaling">
                                    Close
                                  </Trans>
                                ) : (
                                  <Trans i18nKey="createSubPage.action.preview" ns="scaling">
                                    Preview
                                  </Trans>
                                )}
                              </div>
                              {metricPreviews.length > 1 && (
                                <div
                                  className="t-x-sb text-error cursor-pointer"
                                  onClick={() => {
                                    setMetrics(metrics.filter((_, i) => i !== index))
                                    setMetricPreviews(metricPreviews.filter((_, i) => i !== index))
                                  }}
                                >
                                  <Trans i18nKey="createSubPage.action.remove" ns="scaling">
                                    Remove
                                  </Trans>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                        {metricPreviews[index].isOpen && (
                          <tr>
                            <td colSpan={11} className="px-4 pb-4">
                              <div className="rounded-xl ">
                                {metricPreviews[index].loading && (
                                  <div className="w-full flex items-center justify-center p-8">
                                    <Loading />
                                  </div>
                                )}
                                {!metricPreviews[index].loading && (
                                  <>
                                    <div className="rounded-t-xl bg-[#3e4149] py-1 px-4 t-xs text-white flex items-center justify-between">
                                      <div className="flex space-x-1">
                                        <div>Response result：</div>
                                        <div>{metricPreviews[index].result.signal}</div>
                                        <div>{metricPreviews[index].result.status}</div>
                                      </div>
                                      <div
                                        className="cursor-pointer pl-4"
                                        onClick={() => {
                                          onPreviewClick(index)
                                        }}
                                      >
                                        <RefreshIcon />
                                      </div>
                                    </div>
                                    <div className="rounded-b-xl bg-black py-2 px-4 t-xs font-[400] text-secondary max-h-40 overflow-y-auto">
                                      <pre>
                                        <code>{metricPreviews[index].result.data}</code>
                                      </pre>
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
                <div
                  className="mt-2 text-highlight flex space-x-2 items-center cursor-pointer"
                  onClick={() => {
                    setAddMetricModalOpen(true)
                  }}
                >
                  <AddIcon height={16} width={16} />
                  <span className="t-x-sb">
                    <Trans i18nKey="createSubPage.form.moreLoadMetricBtn" ns="scaling">
                      More Load Metric
                    </Trans>
                  </span>
                </div>
              </>
            )}
          </div>

          <Form.Item>
            <div className="pt-4 flex items-end justify-end gap-4">
              <Button
                type="outline"
                className="h-11 px-4 py-3 t-sb"
                onClick={() => {
                  navigate(scaleListPath)
                }}
              >
                <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
              </Button>
              <Button
                type="secondary"
                className="h-11 px-4 py-3 t-sb"
                disabled={loading}
                onClick={() => {
                  form.validateFields().then(() => {
                    if (metrics.length === 0) {
                      setMissingMetricModalOpen(true)
                      return
                    }
                    handleSubmit(form.getFieldsValue())
                  })
                }}
              >
                <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>

      {missingMetricModalOpen && (
        <MissingMetricModal
          onClose={() => setMissingMetricModalOpen(false)}
          onOk={() => {
            setMissingMetricModalOpen(false)
            setAddMetricModalOpen(true)
          }}
        />
      )}

      {addMetricModalOpen && (
        <AddMetricModal
          onClose={() => {
            setAddMetricModalOpen(false)
            setEditingMetric(undefined)
          }}
          onOk={(newMetric: Metric) => {
            handleSaveMetric(newMetric)
            setAddMetricModalOpen(false)
            setEditingMetric(undefined)
          }}
          editingMetric={editingMetric}
        />
      )}
    </>
  )
}
